/**
 * Статусы оплаты, означающие что заказ оплачен.
 * Бэкенд в разных местах отдаёт либо 'paid', либо сырой статус провайдера 'succeeded' —
 * считаем оплаченным любой из них, чтобы сверка не промахнулась.
 */
export const PAID_PAYMENT_STATUSES = new Set(['paid', 'succeeded'])

export function isPaidPaymentStatus(paymentStatus?: string | null): boolean {
  return !!paymentStatus && PAID_PAYMENT_STATUSES.has(String(paymentStatus))
}

interface PollingOrderSummary {
  id: string | number
  paymentStatus?: string | null
}

export interface PaidSinceLastPollResult {
  /** Заказы, которые только что перешли из «ждёт оплаты» в оплаченные */
  paidOrderIds: string[]
  /** Кого продолжаем ждать — передаётся в следующий опрос */
  stillAwaiting: Set<string>
}

/**
 * Ищет заказы, оплаченные между двумя опросами.
 *
 * Зачем: оплата может пройти мимо приложения (ссылка pay.tbank.ru, возврат не состоялся),
 * и тогда корзину надо очистить по факту оплаты. Но список retry-payment отдаёт и уже
 * оплаченные заказы, поэтому «оплачен» сам по себе не является событием: раньше сверка
 * запускалась на каждом опросе по последнему заказу из списка и стирала корзину при
 * каждом старте приложения у любого, у кого есть оплаченный заказ в истории.
 *
 * Событие — это ПЕРЕХОД: заказ был виден неоплаченным, а теперь оплачен или пропал из
 * списка. Только такие заказы возвращаются на сверку.
 */
export function findOrdersPaidSinceLastPoll(
  summaries: PollingOrderSummary[],
  awaitingPayment: ReadonlySet<string>,
): PaidSinceLastPollResult {
  const stillAwaiting = new Set(awaitingPayment)
  const paidOrderIds: string[] = []
  const currentIds = new Set(summaries.map(summary => String(summary.id)))

  for (const summary of summaries) {
    const orderId = String(summary.id)

    if (!isPaidPaymentStatus(summary.paymentStatus)) {
      stillAwaiting.add(orderId)
      continue
    }

    if (stillAwaiting.delete(orderId)) {
      paidOrderIds.push(orderId)
    }
  }

  // Заказ пропал из списка — считаем, что он ушёл в оплаченные.
  for (const orderId of stillAwaiting) {
    if (!currentIds.has(orderId)) {
      stillAwaiting.delete(orderId)
      paidOrderIds.push(orderId)
    }
  }

  return { paidOrderIds, stillAwaiting }
}
